import { checkWebAccessApi } from "@/api/services/main/auth";
import { ERROR_STATUS_CODES } from "@/constants/error";
import { isNativeWebView } from "@/hooks/useNativeBridge";
import { redirectToStoreForWebAccess } from "@/utils/api";
import { isApiError, showError } from "@/utils/error";
import { isValidLearnerAccountId } from "@/utils/learner-account-id";
import { useEffect, useState } from "react";

export enum EWebAccessCheckStatus {
  CHECKING = "checking",
  ALLOWED = "allowed",
  BLOCKED = "blocked",
}

/**
 * Web login with learner id in the URL: verify id format + web access before showing the password field.
 * Failure toasts and redirects to the store. Skipped in the native app or without an id.
 */
export const useWebAccessCheck = (learnerId: string): EWebAccessCheckStatus => {
  const needsCheck = !!learnerId && !isNativeWebView();
  const [status, setStatus] = useState<EWebAccessCheckStatus>(needsCheck ? EWebAccessCheckStatus.CHECKING : EWebAccessCheckStatus.ALLOWED);

  useEffect(() => {
    if (!needsCheck) return;
    let ignore = false;

    if (!isValidLearnerAccountId(learnerId)) {
      setStatus(EWebAccessCheckStatus.BLOCKED);
      redirectToStoreForWebAccess();
      return;
    }

    (async () => {
      try {
        await checkWebAccessApi({ accountId: learnerId });
        if (!ignore) setStatus(EWebAccessCheckStatus.ALLOWED);
      } catch (error) {
        if (ignore) return;
        if (isApiError(error) && error.code === ERROR_STATUS_CODES.WEB_ACCESS_NOT_ALLOWED) {
          // Interceptor already toasted and scheduled the store redirect
          setStatus(EWebAccessCheckStatus.BLOCKED);
          return;
        }
        // Anything else (network, unexpected): let the user try logging in, /login runs the same check
        showError(error);
        setStatus(EWebAccessCheckStatus.ALLOWED);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [learnerId, needsCheck]);

  return status;
};
